import { clsx } from 'clsx';
import { Check, CircleMinus, CirclePlus } from 'lucide-react';
import { Brand, Category } from '@/payload-types';
import { Fragment, MouseEventHandler, useState } from 'react';

interface IFilterButtonProps {
  item: (Category | Brand);
  onClick?: MouseEventHandler<HTMLButtonElement>;
  className?: string;
  searchString?: string;
}

function ProductListFilterButton(props: IFilterButtonProps) {
  const { item, onClick, className, searchString } = props;

  return (
    <button
      className={clsx('flex items-center gap-2.5 cursor-pointer', className)}
      onClick={onClick}
    >
      <div
        className={clsx(
          'flex w-5 h-5 items-center justify-center rounded-md border-2',
          searchString?.includes(item.id)
            ? 'border-secondary-400 bg-secondary-400'
            : 'border-neutral-200 bg-neutral-100',
        )}
      >
        {searchString?.includes(item.id) && <Check width={12} height={12} color="white" />}
      </div>
      <span className="text-sm whitespace-no-breaks text-start flex-1">{item.title}</span>
    </button>
  );
}

interface IProductListFilterItemProps {
  item: (Category | Brand) & { children?: (Category | Brand)[] };
  onClickWrapper: (id: string, queryKey: 'c' | 'b') => MouseEventHandler<HTMLButtonElement>;
  className?: string;
  searchString?: string;
  queryKey: 'c' | 'b'
}

export default function ProductListFilterItem(props: IProductListFilterItemProps) {
  const { item, searchString, onClickWrapper, queryKey } = props;

  const [expanded, setExpanded] = useState(true);

  const hasChildren = Boolean(item.children?.length);

  return (
    <Fragment>
      <div className="flex items-center justify-between">
        <ProductListFilterButton
          item={item}
          onClick={onClickWrapper(item.id, queryKey)}
          searchString={searchString}
        />
        {hasChildren && (
          <button className="cursor-pointer" onClick={() => setExpanded(!expanded)}>
            {expanded
              ? <CircleMinus className="text-secondary" width={16} height={16} />
              : <CirclePlus width={16} height={16} />
            }
          </button>
        )}
      </div>
      {hasChildren && (
        <div
          className={clsx(
            'grid grid-rows-0fr transition-all duration-500',
            expanded && 'grid-rows-1fr',
          )}
        >
          <div className="overflow-hidden">
            {item.children?.map( childItem => (
              <ProductListFilterButton
                key={childItem.id}
                item={childItem}
                onClick={onClickWrapper(childItem.id, queryKey)}
                searchString={searchString}
                className="ml-6"
              />
            ))}
          </div>
        </div>
      )}
    </Fragment>
  );
}
